import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../services/api";
import { Button } from "../components/ui/Button";

const defaultForm = {
  company_name: "",
  cnpj: "",
  slug: "",
  is_active: true,
};

const Tenants = () => {
  const [tenants, setTenants] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [editingTenant, setEditingTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [activeTenantId, setActiveTenantId] = useState(localStorage.getItem("active_tenant_id") || "");

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await api.get("/tenants/");
      setTenants(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Erro ao carregar empresas:", err);
      setError("Erro ao carregar empresas.");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(defaultForm);
    setEditingTenant(null);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const openEdit = (tenant) => {
    setEditingTenant(tenant);
    setForm({
      company_name: tenant.company_name || "",
      cnpj: tenant.cnpj || "",
      slug: tenant.slug || "",
      is_active: !!tenant.is_active,
    });
    setMessage("");
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setMessage("");
      setError("");

      const payload = {
        company_name: form.company_name,
        cnpj: form.cnpj || null,
        slug: form.slug || null,
        is_active: !!form.is_active,
      };

      if (editingTenant) {
        await api.put(`/tenants/${editingTenant.id}`, payload);
        setMessage("Empresa atualizada com sucesso.");
      } else {
        await api.post("/tenants/", payload);
        setMessage("Empresa cadastrada com sucesso.");
      }

      resetForm();
      await fetchTenants();
    } catch (err) {
      console.error("Erro ao salvar empresa:", err);
      setError(err.response?.data?.detail || "Erro ao salvar empresa.");
    } finally {
      setSaving(false);
    }
  };

  const openSettings = (tenant) => {
    selectTenant(tenant, false);
    window.location.href = "/settings";
  };

  const selectTenant = (tenant, showMessage = true) => {
    localStorage.setItem("active_tenant_id", String(tenant.id));
    localStorage.setItem("active_tenant_name", tenant.company_name || "");
    localStorage.setItem("active_tenant_slug", tenant.slug || "");
    setActiveTenantId(String(tenant.id));

    if (showMessage) {
      setMessage(`Empresa ativa alterada para ${tenant.company_name}.`);
      setError("");
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Empresas</h1>
          <p className="text-gray-500">
            Cadastre e gerencie as empresas do ambiente multitenant.
          </p>
        </div>

        <div className="rounded-lg border bg-white p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {editingTenant ? "Editar empresa" : "Nova empresa"}
          </h2>

          {message && (
            <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-green-700">
              {message}
            </div>
          )}

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Nome da empresa
              </label>
              <input
                type="text"
                value={form.company_name}
                onChange={(e) => handleChange("company_name", e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                CNPJ
              </label>
              <input
                type="text"
                value={form.cnpj}
                onChange={(e) => handleChange("cnpj", e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2"
                placeholder="00.000.000/0000-00"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Slug
              </label>
              <input
                type="text"
                value={form.slug}
                onChange={(e) => handleChange("slug", e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2"
                placeholder="empresa-exemplo"
              />
            </div>

            <div className="flex items-center">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={!!form.is_active}
                  onChange={(e) => handleChange("is_active", e.target.checked)}
                />
                Empresa ativa
              </label>
            </div>

            <div className="md:col-span-2 flex gap-2 justify-end">
              {editingTenant && (
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancelar edição
                </Button>
              )}

              <Button type="submit" disabled={saving}>
                {saving
                  ? "Salvando..."
                  : editingTenant
                  ? "Salvar alterações"
                  : "Cadastrar empresa"}
              </Button>
            </div>
          </form>
        </div>

        <div className="rounded-lg border bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Empresas cadastradas</h2>

          {loading ? (
            <div className="text-gray-500">Carregando empresas...</div>
          ) : tenants.length === 0 ? (
            <div className="text-gray-500">Nenhuma empresa cadastrada.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[680px]">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Nome
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      CNPJ
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Slug
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Status
                    </th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((tenant) => (
                    <tr key={tenant.id} className="border-b border-gray-100">
                      <td className="py-3 px-4 text-sm text-gray-900">{tenant.company_name}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{tenant.cnpj || "—"}</td>
                      <td className="py-3 px-4 text-sm text-gray-600">{tenant.slug}</td>
                      <td className="py-3 px-4 text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            tenant.is_active
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {tenant.is_active ? "Ativa" : "Inativa"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            type="button"
                            variant={String(tenant.id) === String(activeTenantId) ? "default" : "outline"}
                            onClick={() => selectTenant(tenant)}
                          >
                            {String(tenant.id) === String(activeTenantId) ? "Empresa ativa" : "Selecionar"}
                          </Button>
                          <Button type="button" variant="outline" onClick={() => openEdit(tenant)}>
                            Editar
                          </Button>
                          <Button type="button" variant="outline" onClick={() => openSettings(tenant)}>
                            Configurações
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Tenants;
